import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { getMenuData } from '../../utils/menuHelper';
import { noopHook } from '../../utils/noop';
import { FAIcon } from '../fa-icon';
import TopRightMenu from '../Headers/TopRightMenu';
import { createDefaultHeader, createLearnerHeader, isObjectWithNoProperties } from './utils';
import TopLeftMenu from '../Headers/TopLeftMenu';
import { HeaderMenu } from '../HeaderMenu';
import { contentUserNavigation } from '../../utils/menus/content_user_navigation_menu';
import { i18n } from '../../i18n/translate';

export function HeaderMenuWrapper({
  appendLanguage = true,
  authCapabilities,
  authUrls,
  content,
  currentUrl,
  customLeft,
  customRight,
  environment,
  helpMessage,
  helpMessageEnabled,
  hideImpersonation = false,
  hideLogin,
  idProvidersUrl,
  isDashiFi = false,
  isFoundry = false,
  isHomeroom = false,
  isMobile = false,
  isUnauthenticated,
  languages,
  loginText,
  logoOverrideHyperlink,
  logoOverrideUrl,
  onExitImpersonation,
  onLoginLoad,
  organization,
  program,
  selectedLanguage,
  service,
  slug,
  socialIcon,
  theme,
  user,
  renderAsLegacy = true,
  useSearchParams = noopHook,
}) {
  const isEverfiAdmin = !organization && !isHomeroom;
  const isNextAdmin = isEverfiAdmin && service === 'next';
  const isDeactivatedOrg = !isUnauthenticated && organization?.active === false;
  const hideMenu = isUnauthenticated && isEverfiAdmin;
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasColors, setHasColors] = useState(isObjectWithNoProperties(program) || program?.primary_color === null);
  const navbarRef = useRef(null);
  const [searchParams] = useSearchParams();

  let currentRuleSetName = null;
  const isImpersonating = !!user?.impersonator_id;
  const isCustom = customLeft || customRight;

  if (searchParams) {
    currentRuleSetName = searchParams.get('current_rule_set_name');
  }
  const buildMenuData = useCallback(() => {
    if (slug) {
      return getMenuData({
        slug,
        environment,
        user,
        organization,
        currentRuleSetName,
        isAdmin: isEverfiAdmin,
        locale: selectedLanguage,
        currentUrl,
        service,
      });
    }
  }, [slug, environment, user, organization, currentRuleSetName, isEverfiAdmin, selectedLanguage, currentUrl]);

  const [menuData, setMenuData] = useLocalStorage(`${slug}-data`, buildMenuData());

  let observer;
  // Create an instance of MutationObserver
  if (typeof window !== 'undefined') {
    const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
    observer = new MutationObserver(mutations => handleMutations(mutations));

    /**
     * We are watching for Next to inject customer colors into
     * the head of the document. We have to wait to display some
     * header elements until this happens.
     */
    // Start observing the target node for configured mutations
    const targetNode = document.head;
    observer.observe(targetNode, { childList: true, subtree: true });
  }

  /**
   * Removes DOM mutation event listener
   */
  const removeStyleTagEventListener = () => {
    if (observer) {
      observer.disconnect();
    }
  };

  /**
   * Handler for Next DOM injection
   * Sets hasColors = true if injected style element detected
   * @param {*} mutations
   */
  const handleStyleTagInjection = mutations => {
    // this is the element injected by Next
    const styleTag = mutations?.addedNodes?.[0]?.id?.includes('custom-colors') !== -1;

    // injected element detected
    if (styleTag) {
      removeStyleTagEventListener();
      // there is a CSS transition of 0.15 seconds
      // this delays displaying the buttons until
      // transition has completed
      setTimeout(() => {
        setHasColors(true);
      }, 250);
    }
  };

  const handleMutations = mutationsList => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' || mutation.type === 'subtree') {
        handleStyleTagInjection(mutation);
      }
    }
  };

  useEffect(() => {
    // if primary_color is null, then we use default colors
    if (!hasColors && (isObjectWithNoProperties(program) || program?.primary_color === null)) {
      setHasColors(true);
    }
  }, []);

  useEffect(() => {
    setMenuData(buildMenuData());
  }, [currentRuleSetName, organization, setMenuData, slug, user, buildMenuData]);

  useEffect(() => {
    if (isDeactivatedOrg && !document.body.classList.contains('deactivated')) {
      document.body.classList.add('deactivated');
    }
  }, []);

  const getRoleListForUi = () => {
    const roleList = [];
    user.rule_sets.map(rs => {
      if (rs.roles) {
        rs.roles.forEach(role => {
          if (role.slug === user.rule_set_roles[rs.id]) {
            roleList.push(`${rs.label} : ${rs.role || ''}`);
          }
        });
      } else {
        roleList.push(`${rs.label} : ${rs.role}`);
      }
    });
    return roleList.join(', ');
  };

  const isLearner = () => {
    let learner = false;
    if (user?.user_rule_set_roles) {
      Object.values(user.user_rule_set_roles).forEach(value => {
        learner = value.role === 'learner';
      });
    }
    return learner || user?.guest;
  };

  const isK12User = () => user?.user_rule_set_roles?.find(({ role }) => role === 'k12') && isHomeroom;

  const handleBeforeExitImpersonation = () => {
    localStorage.removeItem('current_rule_set_name');
    if (onExitImpersonation) {
      onExitImpersonation();
    }
  };

  const renderImpersonationBanner = (config = {}) => {
    if (hideImpersonation) return null;

    const { className = '' } = config;

    const organizationName = organization?.name || user?.organization_name;
    const exitImpersonationUrl = isHomeroom
      ? `${environment.HOMEROOM_ADMIN_URL}/admin/impersonate/destroy`
      : `${environment.MENU_DOMAIN_FOR_ADMINIFI}/admin/impersonation/sign_out`;
    const showExitImpersonation = !!(environment.MENU_DOMAIN_FOR_ADMINIFI || environment.MENU_DOMAIN_FOR_HOMEROOM);

    return (
      <div className={`impersonating-header p3 ${className}`} data-testid="impersonating-header">
        <div className="impersonation-label bold">
          <FAIcon icon="mask" />
          Impersonate Mode
        </div>

        {organizationName && <div className="impersonation-org"> Organization: {organizationName} </div>}

        {user && !isK12User() && <div className="impersonation-roles">{getRoleListForUi()}</div>}

        {showExitImpersonation && (
          <div className="impersonation-exit ui-compact">
            <a onClick={handleBeforeExitImpersonation} href={exitImpersonationUrl}>
              <FAIcon icon="power-off" /> Exit
            </a>
          </div>
        )}
      </div>
    );
  };

  const renderDeactivatedOrgBanner = () => {
    return (
      <div className={'deactivated-organization-header p3'} data-testid="deactivated-organization-header">
        <div className="deactivated-organization-label">
          <FAIcon icon="triangle-exclamation" />
          {i18n.t('Account Deactivated')}
        </div>
        <div className="deactivated-organization-desc">
          {user?.is_manager
            ? i18n.t('Please reach out to your customer support representative for assistance.')
            : i18n.t('Please reach out to your program administrator.')}
        </div>
      </div>
    );
  };

  const rightMenu = (extras = {}) => {
    return (
      <TopRightMenu
        adminifiDomain={environment.MENU_DOMAIN_FOR_ADMINIFI}
        homeroomDomain={environment.MENU_DOMAIN_FOR_HOMEROOM}
        authCapabilities={authCapabilities}
        authUrls={authUrls}
        content={content}
        customRight={customRight}
        helpMessage={helpMessage}
        helpMessageEnabled={helpMessageEnabled}
        hideLogin={hideLogin}
        hideMenu={hideMenu}
        idProvidersUrl={idProvidersUrl}
        isFoundry={isFoundry}
        isHomeroom={isHomeroom}
        isImpersonating={isImpersonating}
        isMobile={isMobile}
        languages={languages}
        loginText={loginText}
        menuData={menuData}
        onLoginLoad={onLoginLoad}
        onStopImpersonation={handleBeforeExitImpersonation}
        selectedLanguage={selectedLanguage}
        renderAsLegacy={renderAsLegacy}
        socialIcon={socialIcon}
        user={user}
        {...extras}
      />
    );
  };

  const leftMenu = (extras = {}) => {
    return (
      <TopLeftMenu
        isDashiFi={isDashiFi}
        isMobile={isMobile}
        isImpersonating={isImpersonating}
        customLeft={customLeft}
        isNextAdmin={isNextAdmin}
        isEverfiAdmin={isEverfiAdmin}
        isUnauthenticated={isUnauthenticated}
        logoOverrideHyperlink={logoOverrideHyperlink}
        logoOverrideUrl={logoOverrideUrl}
        organization={organization}
        {...extras}
      />
    );
  };

  if (!menuData) return <header id="header" />;

  const getHeaderMenu = () => {
    return (
      !!contentUserNavigation &&
      !hideMenu &&
      !!content && (
        <HeaderMenu
          additionalNavClass="content-nav"
          isImpersonating={isImpersonating}
          isMobile={isMobile}
          menuData={contentUserNavigation}
          currentRuleSets={user?.rule_sets ?? []}
          helpMessage={helpMessage}
          helpMessageEnabled={helpMessageEnabled}
          organizationSlug={user?.organization_slug}
          user={user}
          adminifiDomain={environment.MENU_DOMAIN_FOR_ADMINIFI}
          languages={languages}
          renderAsLegacy={renderAsLegacy}
          selectedLanguage={selectedLanguage}
          appendLanguage={appendLanguage}
        />
      )
    );
  };

  const config = {
    content,
    currentUrl,
    customRight,
    environment,
    hasColors,
    helpMessage,
    helpMessageEnabled,
    hideMenu,
    isCustom,
    isDashiFi,
    isImpersonating,
    isLearner,
    isMobile,
    isMobileMenuOpen,
    isEverfiAdmin,
    isNextAdmin,
    isDeactivatedOrg,
    languages,
    leftMenu,
    menu: getHeaderMenu(),
    navbarRef,
    program,
    renderImpersonationBanner,
    renderDeactivatedOrgBanner,
    rightMenu,
    selectedLanguage,
    setMobileMenuOpen,
    theme,
    user,
  };

  if (slug.includes('learner')) {
    return createLearnerHeader(config);
  }

  return createDefaultHeader(config);
}

HeaderMenuWrapper.displayName = 'HeaderMenuWrapper';

HeaderMenuWrapper.propTypes = {
  appendLanguage: PropTypes.bool,
  authCapabilities: PropTypes.object,
  authUrls: PropTypes.object,
  content: PropTypes.object,
  currentUrl: PropTypes.string,
  customLeft: PropTypes.string,
  customRight: PropTypes.string,
  environment: PropTypes.object,
  helpMessage: PropTypes.string,
  helpMessageEnabled: PropTypes.bool,
  hideImpersonation: PropTypes.bool,
  renderAsLegacy: PropTypes.bool,
  hideLogin: PropTypes.bool,
  idProvidersUrl: PropTypes.string,
  isDashiFi: PropTypes.bool,
  isFoundry: PropTypes.bool,
  isHomeroom: PropTypes.bool,
  isMobile: PropTypes.bool,
  isUnauthenticated: PropTypes.bool,
  languages: PropTypes.object,
  loginText: PropTypes.string,
  logoOverrideHyperlink: PropTypes.string,
  logoOverrideUrl: PropTypes.string,
  onExitImpersonation: PropTypes.func,
  onLoginLoad: PropTypes.func,
  organization: PropTypes.object,
  program: PropTypes.object,
  selectedLanguage: PropTypes.string,
  service: PropTypes.string,
  slug: PropTypes.string,
  socialIcon: PropTypes.string,
  theme: PropTypes.object,
  user: PropTypes.object,
  useSearchParams: PropTypes.func,
  UtilityFunctions: PropTypes.object,
};
